import { useEffect, useRef } from 'react'
import { toLang } from '../../config/locales'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react'
import { useUiStore } from '../../store/uiStore'
import { useCartStore } from '../../store/cartStore'
import Button from '../ui/Button'
import { formatKr } from '../../lib/shop/format'
import { useCatalogue } from '../../lib/shop/catalogue'
import { useCartDetail, type CartDetailLine } from '../../lib/shop/cartDetail'
import ProductThumb from './ProductThumb'
import { itemFromCartLine, trackCartChange, trackViewCart } from '../../lib/analytics/ecommerce'
import { useLiveStock } from '../../lib/shop/useLiveStock'
import { useStockStore } from '../../store/stockStore'
import { MAX_PER_LINE, addBlock, blockHint } from '../../lib/shop/orderLimits'
import { useDiscount } from '../../lib/shop/useDiscount'
import { discountTotals, minusKr } from '../../lib/shop/discountCode'
import DiscountCodeField from './DiscountCodeField'

export default function CartDrawer() {
  const { lang = 'en' } = useParams<{ lang: string }>()
  const locale = toLang(lang)
  const { t } = useTranslation()
  const reduce = useReducedMotion()
  const navigate = useNavigate()

  const isOpen = useUiStore((s) => s.isCartDrawerOpen)
  const setOpen = useUiStore((s) => s.setCartDrawer)
  const updateQuantity = useCartStore((s) => s.updateQuantity)
  const removeItem = useCartStore((s) => s.removeItem)

  const catalogue = useCatalogue()
  const { lines, subtotal, hasOrphans } = useCartDetail(catalogue)
  const count = lines.reduce((sum, l) => sum + l.quantity, 0)
  // BUG-518: the discount code, shared with checkout. Shipping is not known here, so the total is the
  // goods after the discount; checkout adds the shipping.
  const discount = useDiscount(lines.filter((l) => !l.orphaned), locale)
  const totals = discountTotals(subtotal, 0, discount.applied)
  // BUG-453: live stock each time the drawer opens (adding an item opens it too).
  useLiveStock(isOpen)
  const live = useStockStore((s) => s.live)
  const adjusted = useStockStore((s) => s.adjusted)
  const forget = useStockStore((s) => s.forget)
  const maxOf = (item: { sku: string; max: number }) => (live && item.sku in live ? Math.max(0, live[item.sku]) : item.max)
  // BUG-473/520: why "+" is off for a line: the 20 cap, none left, or the basket already holds all we have.
  const plusHint = (item: { sku: string; max: number; quantity: number }) => {
    const block = addBlock(item.quantity, maxOf(item), 0)
    if (!block) return undefined
    const hint = blockHint(block)
    return t(hint.key, hint.values)
  }
  const setQuantity = (item: CartDetailLine, quantity: number) => {
    forget(item.key)
    updateQuantity(item.key, quantity)
    trackCartChange(item, quantity, catalogue)
  }
  const removeLine = (item: CartDetailLine) => {
    removeItem(item.key)
    trackCartChange(item, 0, catalogue)
  }

  // GA4 view_cart (BUG-479): once per opening, when the basket's prices are known.
  const viewSent = useRef(false)
  useEffect(() => {
    if (!isOpen) {
      viewSent.current = false
      return
    }
    if (viewSent.current || catalogue.phase !== 'ready' || lines.length === 0) return
    viewSent.current = true
    trackViewCart(lines.filter((l) => !l.orphaned).map((l) => itemFromCartLine(l, catalogue)))
  }, [isOpen, catalogue, lines])

  const close = () => setOpen(false)
  const goToCheckout = () => {
    close()
    navigate(`/${lang}/shop/checkout`)
  }

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex justify-end"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          aria-modal="true"
          role="dialog"
          aria-label={t('cart.title')}
        >
          {/* Backdrop */}
          <button
            type="button"
            aria-label={t('cart.close')}
            onClick={close}
            className="absolute inset-0 bg-mocha/80 backdrop-blur-sm"
          />

          {/* Panel */}
          <motion.div
            initial={{ x: reduce ? 0 : '100%' }}
            animate={{ x: 0 }}
            exit={{ x: reduce ? 0 : '100%' }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative flex h-full w-full max-w-md flex-col bg-ivory shadow-[0_20px_60px_rgba(45,28,18,0.35)]"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-mocha/10 px-6 py-5">
              <h2 className="font-display text-2xl text-mocha">
                {t('cart.title')}
                {count > 0 && <span className="ml-2 font-body text-sm text-mocha/40">({count})</span>}
              </h2>
              <button
                type="button"
                onClick={close}
                aria-label={t('cart.close')}
                className="text-mocha/50 transition-colors hover:text-gold"
              >
                <X size={22} />
              </button>
            </div>

            {lines.length === 0 ? (
              /* Empty state */
              <div className="flex flex-1 flex-col items-center justify-center gap-5 px-6 text-center">
                <ShoppingBag size={40} className="text-mocha/20" />
                <p className="font-body text-sm text-mocha/55">{t('cart.empty')}</p>
                <Button as={Link} to={`/${lang}/shop`} variant="secondary" size="sm" onClick={close}>
                  {t('cart.emptyCta')}
                </Button>
              </div>
            ) : (
              <>
                {/* Items */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                  <ul className="space-y-5">
                    {lines.map((item) => (
                      <li key={item.key} className="flex gap-4" data-sold-out={maxOf(item) <= 0 || undefined}>
                        <Link
                          to={`/${lang}/shop/${item.productId}`}
                          onClick={close}
                          className={`h-20 w-20 flex-shrink-0 overflow-hidden border border-mocha/10 bg-ivory-dark/30 ${maxOf(item) <= 0 ? 'opacity-50 grayscale' : ''}`}
                        >
                          <ProductThumb
                            url={item.image}
                            alt={item.name}
                            className="h-full w-full object-contain p-1.5"
                            fallback={
                              <span className="flex h-full items-center justify-center text-mocha/20">
                                <ShoppingBag size={20} />
                              </span>
                            }
                          />
                        </Link>

                        <div className="flex min-w-0 flex-1 flex-col">
                          <Link
                            to={`/${lang}/shop/${item.productId}`}
                            onClick={close}
                            className="font-display text-sm text-mocha transition-colors hover:text-gold line-clamp-2"
                          >
                            {item.name}
                          </Link>
                          {item.variantName && (
                            <span className="font-body text-xs text-mocha/45">{item.variantName}</span>
                          )}
                          {item.orphaned ? (
                            <p className="mt-1 font-body text-xs text-gold">{t('cart.orphaned')}</p>
                          ) : (
                            <span className="mt-0.5 font-body text-xs text-gold">
                              {formatKr(item.unitPrice, locale)}
                            </span>
                          )}

                          {/* BUG-453: sold out stays greyed for the customer to remove; a line over stock was
                              lowered to what is left and says so. */}
                          {maxOf(item) <= 0 ? (
                            <p className="mt-1 flex items-center gap-3 font-body text-xs" data-stock-issue={item.key}>
                              <span className="font-semibold text-red-700">{t('checkout.soldOut')}</span>
                              <button
                                type="button"
                                onClick={() => removeLine(item)}
                                className="text-gold-ink underline underline-offset-2 hover:text-gold"
                              >
                                {t('checkout.removeLine')}
                              </button>
                            </p>
                          ) : adjusted[item.key] !== undefined ? (
                            <p className="mt-1 font-body text-xs text-gold-ink" data-stock-adjusted={item.key}>
                              {t('stock.adjusted', { count: adjusted[item.key] })}
                            </p>
                          ) : item.quantity >= MAX_PER_LINE ? (
                            /* BUG-473: the order API takes at most 20 of one item. */
                            <p className="mt-1 font-body text-xs text-mocha/55" data-qty-cap={item.key}>
                              {t('stock.maxPerLine', { count: MAX_PER_LINE })}
                            </p>
                          ) : null}
                          <div className="mt-auto flex items-center justify-between pt-2">
                            {/* Quantity stepper */}
                            <div className="flex items-center border border-mocha/15">
                              <button
                                type="button"
                                aria-label={t('cart.decrease')}
                                onClick={() => setQuantity(item, item.quantity - 1)}
                                className="flex h-7 w-7 items-center justify-center text-mocha/60 transition-colors hover:text-gold"
                              >
                                <Minus size={13} />
                              </button>
                              <span className="min-w-[1.75rem] text-center font-body text-sm text-mocha">
                                {item.quantity}
                              </span>
                              <button
                                type="button"
                                aria-label={t('cart.increase')}
                                disabled={item.quantity >= Math.min(maxOf(item), MAX_PER_LINE)}
                                title={plusHint(item)}
                                onClick={() => setQuantity(item, item.quantity + 1)}
                                className="flex h-7 w-7 items-center justify-center text-mocha/60 transition-colors hover:text-gold disabled:opacity-30"
                              >
                                <Plus size={13} />
                              </button>
                            </div>

                            <button
                              type="button"
                              aria-label={t('cart.remove')}
                              onClick={() => removeLine(item)}
                              className="text-mocha/35 transition-colors hover:text-gold"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Footer */}
                <div className="border-t border-mocha/10 px-6 py-5">
                  <div className="mb-3">
                    <DiscountCodeField discount={discount} locale={locale} idPrefix="cart" />
                  </div>
                  {discount.applied ? (
                    <dl className="mb-1 space-y-1 font-body text-sm">
                      <div className="flex justify-between gap-3 text-mocha/60">
                        <dt>{t('cart.subtotal')}</dt>
                        <dd>{formatKr(subtotal, locale)}</dd>
                      </div>
                      <div className="flex justify-between gap-3 text-mocha" data-discount-line>
                        <dt className="min-w-0 break-all">{t('discount.line', { code: discount.code })}</dt>
                        <dd className="whitespace-nowrap">{minusKr(totals.discount, locale)}</dd>
                      </div>
                      <div className="flex items-baseline justify-between gap-3">
                        <dt className="text-mocha/60">{t('discount.total')}</dt>
                        <dd className="font-display text-xl text-mocha">{formatKr(totals.total, locale)}</dd>
                      </div>
                    </dl>
                  ) : (
                    <div className="mb-1 flex items-baseline justify-between">
                      <span className="font-body text-sm text-mocha/60">{t('cart.subtotal')}</span>
                      <span className="font-display text-xl text-mocha">{formatKr(subtotal, locale)}</span>
                    </div>
                  )}
                  <p className="mb-4 text-right font-body text-[11px] text-mocha/40">
                    {t('cart.vatNote')} · {t('cart.shippingNote')}
                  </p>

                  <Button
                    as="button"
                    type="button"
                    disabled={hasOrphans}
                    className="w-full justify-center"
                    onClick={goToCheckout}
                  >
                    {t('cart.checkout')}
                  </Button>
                  {hasOrphans && (
                    <p className="mt-2 text-center font-body text-[11px] text-mocha/50">
                      {t('cart.orphanedBlocksCheckout')}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={close}
                    className="mt-3 w-full font-body text-xs text-mocha/50 underline-offset-4 transition-colors hover:text-gold hover:underline"
                  >
                    {t('cart.continue')}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
